/**
 * Alpine Slider Component
 *
 * Hybrid approach: Alpine.js for reactive state + CSS scroll-snap for mechanics.
 * Replaces the store-slider web component with a simpler, more reliable solution.
 *
 * Usage:
 *   <div x-data="slider({ autoplay: true, autoplayInterval: 5000 })" ...>
 *     <div x-ref="track" @scroll.passive="onScroll()" class="store-carousel ...">
 *       <!-- slides -->
 *     </div>
 *   </div>
 *
 * Features:
 * - Reactive state (currentIndex, slideCount, pages)
 * - Autoplay with pause on hover/focus/touch
 * - Reliable initial position via $nextTick
 * - Thumbnail slider sync (leader/follower pattern)
 * - Support for full-bleed/auto-offset edge alignment
 */

document.addEventListener('alpine:init', () => {
  Alpine.data('slider', (config = {}) => ({
    // === State ===
    currentIndex: 0,
    slideCount: 0,
    visibleCount: 1,
    isAutoplayPaused: false,
    isNavigating: false,
    initialized: false,

    // === Config (from x-data) ===
    id: config.id || '',
    autoplay: config.autoplay ?? false,
    autoplayInterval: config.autoplayInterval ?? 5000,
    followerId: config.followerId || '', // data-slider-id for thumbnail sync

    // === Internal ===
    _autoplayTimer: null,
    _resizeObserver: null,
    _scrollRaf: null,
    _navTimeout: null,

    // === Lifecycle ===
    init() {
      // Wait for DOM to settle before calculating
      this.$nextTick(() => {
        this._calculateSlides();
        this._setupResizeObserver();

        // Ensure first slide is visible (fix for video slider bug)
        // Use double $nextTick to ensure videos/images have rendered
        this.$nextTick(() => {
          this.scrollToIndex(0, 'auto');

          // Mark initialized after initial scroll
          requestAnimationFrame(() => {
            this.initialized = true;
          });
        });

        // Start autoplay if enabled
        if (this.autoplay) {
          this._startAutoplay();
        }

        // Listen for follower clicks (thumbnail sync)
        this._setupFollowerSync();
      });
    },

    destroy() {
      this._stopAutoplay();
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
      if (this._scrollRaf) {
        cancelAnimationFrame(this._scrollRaf);
      }
      if (this._navTimeout) {
        clearTimeout(this._navTimeout);
      }
    },

    // === Computed ===
    get pages() {
      if (this.visibleCount >= this.slideCount) return 1;
      return Math.ceil(this.slideCount / this.visibleCount);
    },

    get currentPage() {
      return Math.floor(this.currentIndex / Math.max(1, this.visibleCount));
    },

    get canPrev() {
      return this.currentIndex > 0;
    },

    get canNext() {
      return this.currentIndex < this.slideCount - this.visibleCount;
    },

    get maxIndex() {
      return Math.max(0, this.slideCount - this.visibleCount);
    },

    // === Private Methods ===
    // Get track element - either $refs.track or $el itself (when x-data is on the scrollable element)
    _getTrack() {
      return this.$refs.track || this.$el;
    },

    _calculateSlides() {
      const track = this._getTrack();
      if (!track) return;

      const children = Array.from(track.children).filter(el => el.nodeType === 1);
      this.slideCount = children.length;

      if (!children.length) {
        this.visibleCount = 1;
        return;
      }

      const firstSlide = children[0];
      const slideWidth = firstSlide.offsetWidth;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const containerWidth = track.clientWidth;

      if (slideWidth <= 0) {
        this.visibleCount = 1;
        return;
      }

      // Calculate how many slides fit
      const stride = slideWidth + gap;
      this.visibleCount = Math.max(1, Math.floor((containerWidth + gap) / stride));
    },

    _setupResizeObserver() {
      const track = this._getTrack();
      if (!track || !('ResizeObserver' in window)) return;

      this._resizeObserver = new ResizeObserver(() => {
        this._calculateSlides();
      });
      this._resizeObserver.observe(track);
    },

    _setupFollowerSync() {
      // If this slider has an ID, listen for clicks on followers (thumbnails)
      if (!this.id) return;

      const section = this.$el.closest('[id^="shopify-section-"]') || document;

      section.addEventListener('click', (e) => {
        const dot = e.target.closest(`[data-slider-id="${this.id}"] [data-slide-index]`);
        if (dot) {
          const index = parseInt(dot.getAttribute('data-slide-index'), 10);
          if (!Number.isNaN(index)) {
            this.goToSlide(index);
          }
        }
      });
    },

    _getPaddingLeft() {
      const track = this.$refs.track;
      if (!track) return 0;
      return parseFloat(getComputedStyle(track).paddingLeft) || 0;
    },

    _getSlideLeft(index) {
      const track = this.$refs.track;
      if (!track) return 0;

      const children = Array.from(track.children).filter(el => el.nodeType === 1);
      const slide = children[index];
      if (!slide) return 0;

      const padLeft = this._getPaddingLeft();
      return Math.max(0, slide.offsetLeft - padLeft);
    },

    _getPageStartLeft(page) {
      const startIndex = Math.min(page * this.visibleCount, this.maxIndex);
      return this._getSlideLeft(startIndex);
    },

    // === Navigation Methods ===
    scrollToIndex(index, behavior = 'smooth') {
      const track = this.$refs.track;
      if (!track) return;

      const clampedIndex = Math.max(0, Math.min(index, this.maxIndex));
      const left = this._getSlideLeft(clampedIndex);

      // Mark as navigating to prevent scroll handler interference
      this.isNavigating = true;

      // Temporarily disable snap during programmatic scroll
      const savedSnap = track.style.scrollSnapType;
      track.style.scrollSnapType = 'none';

      track.scrollTo({ left, behavior });
      this.currentIndex = clampedIndex;

      // Re-enable snap after scroll settles
      if (this._navTimeout) clearTimeout(this._navTimeout);
      this._navTimeout = setTimeout(() => {
        track.style.scrollSnapType = savedSnap || '';
        this.isNavigating = false;
        this._updateFollowers();
      }, behavior === 'auto' ? 50 : 350);
    },

    scrollToPage(page, behavior = 'smooth') {
      const clampedPage = Math.max(0, Math.min(page, this.pages - 1));
      const startIndex = Math.min(clampedPage * this.visibleCount, this.maxIndex);
      this.scrollToIndex(startIndex, behavior);
    },

    next() {
      if (!this.canNext) {
        // Loop to start if at end
        if (this.autoplay) {
          this.scrollToIndex(0);
        }
        return;
      }
      const nextIndex = Math.min(this.currentIndex + this.visibleCount, this.maxIndex);
      this.scrollToIndex(nextIndex);
      this._restartAutoplay();
    },

    prev() {
      if (!this.canPrev) return;
      const prevIndex = Math.max(this.currentIndex - this.visibleCount, 0);
      this.scrollToIndex(prevIndex);
      this._restartAutoplay();
    },

    goToPage(page) {
      this.scrollToPage(page);
      this._restartAutoplay();
    },

    goToSlide(index) {
      this.scrollToIndex(index);
      this._restartAutoplay();
    },

    // === Scroll Detection ===
    onScroll() {
      // Skip if we're programmatically navigating
      if (this.isNavigating) return;

      // Debounce scroll updates
      if (this._scrollRaf) return;

      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = null;
        this._detectActiveIndex();
        this._updateFollowers();
      });
    },

    _detectActiveIndex() {
      const track = this.$refs.track;
      if (!track) return;

      const children = Array.from(track.children).filter(el => el.nodeType === 1);
      if (!children.length) return;

      const scrollLeft = track.scrollLeft;
      const padLeft = this._getPaddingLeft();

      let closest = 0;
      let minDelta = Infinity;

      for (let i = 0; i < children.length; i++) {
        const slideLeft = children[i].offsetLeft - padLeft;
        const delta = Math.abs(slideLeft - scrollLeft);
        if (delta < minDelta) {
          minDelta = delta;
          closest = i;
        }
      }

      this.currentIndex = closest;
    },

    _updateFollowers() {
      // Update thumbnail/pagination elements that follow this slider
      if (!this.id) return;

      const section = this.$el.closest('[id^="shopify-section-"]') || document;

      // Update dot pagination
      const dots = section.querySelectorAll(`[data-slider-id="${this.id}"] .store-dot, .store-slider-pagination[data-slider-id="${this.id}"] .store-dot`);
      dots.forEach((dot, i) => {
        const isPage = dot.hasAttribute('data-page-index');
        const targetIndex = isPage ? i : i;
        const isActive = isPage
          ? this.currentPage === i
          : this.currentIndex === i;

        dot.classList.toggle('is-selected', isActive);
        dot.classList.toggle('is-active', isActive);

        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }

        if (!isPage) {
          dot.setAttribute('data-slide-index', String(i));
        }
      });

      // Update follower sliders (thumbnails) - scroll them to show active
      const followerTracks = section.querySelectorAll(`[data-slider-id="${this.id}"].store-carousel, store-slider[data-slider-id="${this.id}"]`);
      followerTracks.forEach(follower => {
        const followerDots = follower.querySelectorAll('.store-dot');
        const activeDot = followerDots[this.currentIndex];
        if (activeDot && follower.scrollWidth > follower.clientWidth) {
          const padLeft = parseFloat(getComputedStyle(follower).paddingLeft) || 0;
          const targetLeft = Math.max(0, activeDot.offsetLeft - padLeft);
          follower.scrollTo({ left: targetLeft, behavior: 'smooth' });
        }
      });

      // Dispatch custom event for any external listeners
      this.$el.dispatchEvent(new CustomEvent('slider:change', {
        bubbles: true,
        detail: {
          index: this.currentIndex,
          page: this.currentPage,
          id: this.id
        }
      }));
    },

    // === Autoplay ===
    _startAutoplay() {
      if (!this.autoplay || this.isAutoplayPaused) return;
      if (this.pages <= 1) return;

      this._stopAutoplay();

      this._autoplayTimer = setInterval(() => {
        if (!this.isAutoplayPaused) {
          this.next();
        }
      }, this.autoplayInterval);
    },

    _stopAutoplay() {
      if (this._autoplayTimer) {
        clearInterval(this._autoplayTimer);
        this._autoplayTimer = null;
      }
    },

    _restartAutoplay() {
      if (this.autoplay && !this.isAutoplayPaused) {
        this._startAutoplay();
      }
    },

    pauseAutoplay() {
      this.isAutoplayPaused = true;
      this._stopAutoplay();
    },

    resumeAutoplay() {
      this.isAutoplayPaused = false;
      if (this.autoplay) {
        this._startAutoplay();
      }
    },

    // === Control Handlers (for external buttons) ===
    handlePrev() {
      this.prev();
    },

    handleNext() {
      this.next();
    },

    handleDotClick(index) {
      this.goToSlide(index);
    },

    handlePageClick(page) {
      this.goToPage(page);
    }
  }));
});

/**
 * Global slider control handler
 * Handles clicks on slider controls that are outside the Alpine scope.
 * Looks for nearest slider in the section and calls its methods.
 */
document.addEventListener('click', (e) => {
  const control = e.target.closest('[data-action="prev"], [data-action="next"]');
  if (!control) return;

  const wrapper = control.closest('.store-slider-controls');
  if (!wrapper) return;

  const sliderId = wrapper.getAttribute('data-slider-id');
  const section = wrapper.closest('[id^="shopify-section-"]') || document;

  let slider = null;

  if (sliderId) {
    slider = section.querySelector(`#${CSS.escape(sliderId)}`);
  }

  // Fallback: find nearest slider in section
  if (!slider) {
    slider = section.querySelector('[x-data*="slider"]');
  }

  if (!slider || !slider._x_dataStack) return;

  const alpineData = slider._x_dataStack[0];
  if (!alpineData) return;

  const action = control.getAttribute('data-action');
  if (action === 'prev' && typeof alpineData.prev === 'function') {
    alpineData.prev();
  } else if (action === 'next' && typeof alpineData.next === 'function') {
    alpineData.next();
  }
});

/**
 * Global pagination click handler
 */
document.addEventListener('click', (e) => {
  const dot = e.target.closest('.store-dot[data-page-index], .store-dot[data-slide-index]');
  if (!dot) return;

  const wrapper = dot.closest('.store-slider-pagination, .store-pagination');
  if (!wrapper) return;

  const sliderId = wrapper.getAttribute('data-slider-id') ||
                   wrapper.closest('[data-slider-id]')?.getAttribute('data-slider-id');
  const section = wrapper.closest('[id^="shopify-section-"]') || document;

  let slider = null;

  if (sliderId) {
    slider = section.querySelector(`#${CSS.escape(sliderId)}`);
  }

  // Fallback: find nearest slider in section
  if (!slider) {
    slider = section.querySelector('[x-data*="slider"]');
  }

  if (!slider || !slider._x_dataStack) return;

  const alpineData = slider._x_dataStack[0];
  if (!alpineData) return;

  const pageIndex = dot.getAttribute('data-page-index');
  const slideIndex = dot.getAttribute('data-slide-index');

  if (pageIndex !== null && typeof alpineData.goToPage === 'function') {
    alpineData.goToPage(parseInt(pageIndex, 10));
  } else if (slideIndex !== null && typeof alpineData.goToSlide === 'function') {
    alpineData.goToSlide(parseInt(slideIndex, 10));
  }
});

/**
 * Alpine Slider Pagination Component
 *
 * Syncs with a slider component to display navigation dots.
 * Dynamically creates dots based on slide/page count.
 *
 * Usage:
 *   <div x-data="sliderPagination({ sliderId: 'my-slider', mode: 'slides' })">
 *     <template x-for="(item, i) in items">
 *       <button @click="goTo(i)" :class="currentIndex === i ? 'active' : ''"></button>
 *     </template>
 *   </div>
 */
document.addEventListener('alpine:init', () => {
  Alpine.data('sliderPagination', (config = {}) => ({
    // === State ===
    items: [],
    currentIndex: 0,

    // === Config ===
    sliderId: config.sliderId || '',
    mode: config.mode || 'slides', // 'slides' or 'pages'

    // === Internal ===
    _slider: null,
    _resizeObserver: null,

    // === Lifecycle ===
    init() {
      this.$nextTick(() => {
        this._findSlider();
        this._syncState();
        this._setupListeners();
        this._setupResizeObserver();
      });
    },

    destroy() {
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
    },

    // === Private Methods ===
    _findSlider() {
      const section = this.$el.closest('[id^="shopify-section-"]') || document;

      if (this.sliderId) {
        this._slider = section.querySelector(`#${CSS.escape(this.sliderId)}`);
      }

      // Fallback: nearest slider in section
      if (!this._slider) {
        this._slider = section.querySelector('[x-data*="slider"]');
      }
    },

    _getSliderData() {
      if (!this._slider || !this._slider._x_dataStack) return null;
      return this._slider._x_dataStack[0];
    },

    _syncState() {
      const data = this._getSliderData();
      if (!data) {
        // Retry after Alpine initializes
        setTimeout(() => this._syncState(), 100);
        return;
      }

      const count = this.mode === 'pages' ? data.pages : data.slideCount;
      const index = this.mode === 'pages' ? data.currentPage : data.currentIndex;

      // Create items array for x-for
      this.items = Array.from({ length: count || 0 }, (_, i) => i);
      this.currentIndex = index || 0;
    },

    _setupListeners() {
      if (!this._slider) return;

      // Listen for slider:change custom event
      this._slider.addEventListener('slider:change', (e) => {
        const { index, page } = e.detail;
        this.currentIndex = this.mode === 'pages' ? page : index;
      });
    },

    _setupResizeObserver() {
      if (!this._slider || !('ResizeObserver' in window)) return;

      // Re-sync on resize (slide count may change)
      this._resizeObserver = new ResizeObserver(() => {
        this._syncState();
      });
      this._resizeObserver.observe(this._slider);
    },

    // === Public Methods ===
    goTo(index) {
      const data = this._getSliderData();
      if (!data) return;

      if (this.mode === 'pages' && typeof data.goToPage === 'function') {
        data.goToPage(index);
      } else if (typeof data.goToSlide === 'function') {
        data.goToSlide(index);
      }

      this.currentIndex = index;
    }
  }));
});
